// @oasis-permission-public: Sam decided on 2026-10-23 that every Oasis user can edit Mirage MCP skills
import { KnownApiError } from "@mirage-web/networking/known-api-error";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Plus, Sparkles } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  Card,
  CardAction,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { Skeleton } from "~/components/ui/skeleton";
import {
  buildNewSkillTemplate,
  formatBytes,
  formatDateTime,
  getMutationErrorMessage,
  isEditLockConflict,
  latestFileChange,
  validateSkillName,
} from "~/modules/mcp-skills/helpers";
import { saveMcpSkillFileMutation } from "~/modules/mcp-skills/mutations";
import { mcpSkillsQuery } from "~/modules/mcp-skills/queries";
import type { McpSkill } from "~/modules/mcp-skills/schemas";

export const Route = createFileRoute("/_dash/mcp/")({
  component: RouteComponent,
});

function RouteComponent() {
  const { data, error, isLoading } = mcpSkillsQuery.useQuery();
  const [newSkillOpen, setNewSkillOpen] = useState(false);

  const skills = data?.skills.slice().sort((a, b) => a.name.localeCompare(b.name)) ?? null;

  return (
    <main className="space-y-6 pb-12">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Mirage MCP skills</h1>
          <p className="text-sm text-muted-foreground">
            Skills served by the Mirage MCP server from the mirage-mcp-skills bucket.
          </p>
        </div>
        <Button onClick={() => setNewSkillOpen(true)}>
          <Plus className="mr-2 size-4" /> New skill
        </Button>
      </div>

      <p className="rounded-md border bg-muted px-4 py-3 text-sm text-muted-foreground">
        Changes take effect after api_gateway restarts.
      </p>

      {isLoading && <SkillCardSkeletons />}
      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error.message}
        </div>
      )}
      {data && skills && skills.length === 0 && (
        <div className="rounded-lg border border-dashed px-6 py-16 text-center">
          <p className="text-sm font-medium">No skills yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            Create the first skill served to agents by the Mirage MCP server.
          </p>
          <Button className="mt-4" onClick={() => setNewSkillOpen(true)}>
            <Plus className="mr-2 size-4" /> New skill
          </Button>
        </div>
      )}
      {skills && skills.length > 0 && (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {skills.map((skill) => (
            <SkillCard key={skill.name} skill={skill} />
          ))}
        </div>
      )}

      <NewSkillDialog open={newSkillOpen} onOpenChange={setNewSkillOpen} />
    </main>
  );
}

function SkillCard({ skill }: { skill: McpSkill }) {
  const lastChange = latestFileChange(skill.files);
  const description = skill.description ?? (skill.error ? null : "Invalid SKILL.md");

  return (
    <Link
      to="/mcp/skills/$skill"
      params={{ skill: skill.name }}
      className="group block h-full rounded-xl focus-visible:outline-none"
    >
      <Card className="h-full gap-3 transition-[border-color,box-shadow] duration-150 group-hover:border-primary/40 group-hover:shadow-sm group-focus-visible:ring-1 group-focus-visible:ring-ring">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
              <Sparkles className="size-4" />
            </span>
            <span className="truncate font-mono text-sm font-semibold">{skill.name}</span>
          </CardTitle>
          <CardAction>
            <Badge variant="outline" title={`${skill.fileCount} of ${skill.limits.maxFiles} files`}>
              {skill.fileCount} / {skill.limits.maxFiles}
            </Badge>
          </CardAction>
          {description ? (
            <CardDescription className="line-clamp-3" title={skill.description ?? undefined}>
              {description}
            </CardDescription>
          ) : null}
          {skill.error && (
            <p
              className="line-clamp-2 rounded-md bg-danger-muted px-2 py-1.5 text-xs text-danger-foreground"
              title={skill.error}
            >
              {skill.error}
            </p>
          )}
        </CardHeader>
        <CardFooter className="mt-auto justify-between gap-2 border-t text-xs text-muted-foreground">
          <span
            className="whitespace-nowrap"
            title={`${skill.totalBytes} bytes of ${skill.limits.maxTotalBytes} allowed`}
          >
            {formatBytes(skill.totalBytes)} / {formatBytes(skill.limits.maxTotalBytes)}
          </span>
          <span className="truncate" title={lastChange.updatedAt ?? undefined}>
            {lastChange.updatedAt
              ? `${formatDateTime(lastChange.updatedAt)}${
                  lastChange.updatedBy ? ` · ${lastChange.updatedBy}` : ""
                }`
              : "—"}
          </span>
        </CardFooter>
      </Card>
    </Link>
  );
}

function SkillCardSkeletons() {
  return (
    <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3" aria-hidden="true">
      {Array.from({ length: 6 }, (_, index) => (
        <Card key={index} className="h-full gap-3">
          <CardHeader>
            <CardTitle>
              <Skeleton className="h-7 w-2/5" />
            </CardTitle>
            <CardDescription>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="mt-1.5 h-4 w-4/5" />
            </CardDescription>
          </CardHeader>
          <CardFooter className="mt-auto border-t">
            <Skeleton className="h-3.5 w-1/2" />
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function NewSkillDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { mutateAsync: saveFile, isPending } = saveMcpSkillFileMutation.useMutation();

  function close() {
    onOpenChange(false);
    setName("");
    setNameError(null);
    setSubmitError(null);
  }

  async function handleCreate() {
    setSubmitError(null);
    const validationError = validateSkillName(name);
    setNameError(validationError);
    if (validationError) {
      return;
    }

    try {
      await saveFile({
        pathParams: { skill: name },
        params: { path: "SKILL.md" },
        body: { text: buildNewSkillTemplate(name), expectedGeneration: 0 },
      });
      toast.success(`Skill ${name} created.`);
      close();
      void navigate({ to: "/mcp/skills/$skill", params: { skill: name } });
    } catch (mutationError) {
      if (mutationError instanceof KnownApiError && mutationError.responseStatus === 409) {
        setSubmitError(
          isEditLockConflict(mutationError)
            ? "Another edit to this skill is in progress. Try again."
            : "A skill with this name already exists."
        );
        return;
      }
      setSubmitError(getMutationErrorMessage(mutationError, "Failed to create the skill."));
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (nextOpen) {
          onOpenChange(true);
        } else {
          close();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New skill</DialogTitle>
          <DialogDescription>
            Creates a directory with a SKILL.md file in the mirage-mcp-skills bucket.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="new-skill-name">Name</Label>
          <Input
            id="new-skill-name"
            value={name}
            onChange={(event) => {
              setName(event.target.value);
              setNameError(null);
              setSubmitError(null);
            }}
            placeholder="my-skill"
            autoComplete="off"
          />
          <p className="text-xs text-muted-foreground">
            Lowercase letters, digits and hyphens. No hyphen at the start or the end, no double
            hyphens. 64 characters or less.
          </p>
          {nameError && <p className="text-xs text-destructive">{nameError}</p>}
          {submitError && <p className="text-xs text-destructive">{submitError}</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={isPending}>
            Cancel
          </Button>
          <Button onClick={() => void handleCreate()} disabled={isPending}>
            {isPending ? "Creating…" : "Create skill"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
